import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookOpen, FileQuestion, Play, FileText, Clock, ChevronRight, ChevronDown, ArrowRight, GalleryHorizontal, Lightbulb, Stethoscope, Eye, Brain, CheckCircle2, Circle, RotateCcw, CalendarClock, RefreshCw, Loader2 } from 'lucide-react';
import type { SuggestedItem } from '@/hooks/useStudentDashboard';
import type { AdaptiveStudyPlan } from '@/lib/studentMetrics';
import type { DailyPlan } from '@/hooks/useDailyStudyPlan';

interface DashboardTodayPlanProps {
  suggestions: SuggestedItem[];
  studyPlan?: AdaptiveStudyPlan | null;
  onNavigate: (moduleId?: string, chapterId?: string, tab?: string, subtab?: string) => void;
  confidenceInsight?: string | null;
  dailyPlan?: DailyPlan | null;
  yesterdayAdherence?: { completed: number; total: number } | null;
  availableMinutes?: number;
  onAvailableMinutesChange?: (minutes: number) => void;
  onRefreshPlan?: () => Promise<void>;
  isRefreshing?: boolean;
}

/** Maps study mode keys to icons */
const studyModeIconMap: Record<string, React.ElementType> = {
  mcq_practice: FileQuestion,
  recall_practice: Brain,
  case_scenarios: FileText,
  clinical_practice: Stethoscope,
  visual_practice: Eye,
  review: GalleryHorizontal,
};

/** Fallback icon map for legacy type field */
const legacyIconMap: Record<string, React.ElementType> = {
  read: BookOpen,
  mcq: FileQuestion,
  video: Play,
  flashcard: GalleryHorizontal,
  review: ArrowRight,
};

function getTaskIcon(item: SuggestedItem): React.ElementType {
  if (item.prescribedStudyMode?.key) {
    return studyModeIconMap[item.prescribedStudyMode.key] ?? BookOpen;
  }
  return legacyIconMap[item.type] ?? BookOpen;
}

const trendIndicator: Record<string, { icon: string; className: string }> = {
  declining: { icon: '↓', className: 'text-destructive' },
  improving: { icon: '↑', className: 'text-emerald-600 dark:text-emerald-400' },
  stable: { icon: '', className: '' },
};

/** Status indicator component */
function TaskStatusDot({ status }: { status?: string }) {
  if (status === 'completed') {
    return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />;
  }
  if (status === 'partial') {
    return (
      <div className="relative w-3.5 h-3.5 flex-shrink-0">
        <Circle className="w-3.5 h-3.5 text-amber-400" />
        <div className="absolute inset-0 overflow-hidden rounded-full" style={{ clipPath: 'inset(50% 0 0 0)' }}>
          <Circle className="w-3.5 h-3.5 text-amber-500 fill-amber-400" />
        </div>
      </div>
    );
  }
  return <Circle className="w-3.5 h-3.5 text-muted-foreground/40 flex-shrink-0" />;
}

/** Find task status from dailyPlan by chapter ID */
function getTaskStatus(dailyPlan: DailyPlan | null | undefined, chapterId?: string): string | undefined {
  if (!dailyPlan || !chapterId) return undefined;
  const task = dailyPlan.tasks.find(t => t.chapter_id === chapterId);
  return task?.status;
}

function isCarriedOver(dailyPlan: DailyPlan | null | undefined, chapterId?: string): boolean {
  if (!dailyPlan || !chapterId) return false;
  const task = dailyPlan.tasks.find(t => t.chapter_id === chapterId);
  return task?.is_carried_over ?? false;
}

/** Extract just the chapter name from a suggestion */
function getChapterName(item: SuggestedItem): string {
  if (item.chapterTitle && !item.chapterTitle.includes(':')) return item.chapterTitle;
  const parts = (item.title ?? '').split(' — ');
  return parts[0].trim() || item.title || 'Chapter';
}

function getActivityLabel(item: SuggestedItem): string {
  if (item.prescribedStudyMode?.label) return item.prescribedStudyMode.label;
  return '';
}

export function DashboardTodayPlan({ suggestions, studyPlan, onNavigate, confidenceInsight, dailyPlan, yesterdayAdherence, availableMinutes = 60, onAvailableMinutesChange, onRefreshPlan, isRefreshing }: DashboardTodayPlanProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [timeChanged, setTimeChanged] = useState(false);

  const handleRefresh = () => {
    if (!onRefreshPlan) return;
    const hasIncomplete = suggestions.some(s => {
      const status = getTaskStatus(dailyPlan, s.chapterId);
      return status === 'pending' || status === 'partial';
    });
    if (hasIncomplete) {
      setShowConfirm(true);
    } else {
      setTimeChanged(false);
      void onRefreshPlan();
    }
  };

  // Check if all tasks are done
  const allDone = useMemo(() => {
    if (!dailyPlan?.tasks?.length || suggestions.length === 0) return false;
    return suggestions.every(s => getTaskStatus(dailyPlan, s.chapterId) === 'completed');
  }, [dailyPlan, suggestions]);

  // Sort suggestions: pending/partial first, completed last
  const sortedSuggestions = useMemo(() => {
    return [...suggestions].sort((a, b) => {
      const statusA = getTaskStatus(dailyPlan, a.chapterId);
      const statusB = getTaskStatus(dailyPlan, b.chapterId);
      const aCompleted = statusA === 'completed' ? 1 : 0;
      const bCompleted = statusB === 'completed' ? 1 : 0;
      return aCompleted - bCompleted;
    });
  }, [suggestions, dailyPlan]);

  if (suggestions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">Today's Study Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            No suggestions available yet. Start exploring your course content to receive personalized recommendations.
          </p>
        </CardContent>
      </Card>
    );
  }

  // All done state
  if (allDone) {
    return (
      <Card>
        <CardContent className="py-6 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <p className="text-lg font-semibold text-foreground">All done for today!</p>
          <p className="text-sm text-muted-foreground mt-1">
            You completed all {dailyPlan?.tasks.length ?? suggestions.length} tasks. Great work!
          </p>
          {onRefreshPlan && (
            <div className="flex gap-2 justify-center mt-4">
              <Button variant="outline" size="sm" onClick={() => { setTimeChanged(false); void onRefreshPlan(); }} disabled={isRefreshing}>
                {isRefreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                Add more study time
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Find first non-completed suggestion as primary
  const primarySuggestion = sortedSuggestions.find(s => getTaskStatus(dailyPlan, s.chapterId) !== 'completed');
  const otherSuggestions = sortedSuggestions.filter(s => s !== primarySuggestion);
  const totalMinutes = studyPlan?.totalEstimatedMinutes ?? suggestions.reduce((sum, s) => sum + (s.estimatedMinutes || 0), 0);
  const insight = studyPlan?.confidenceInsight ?? confidenceInsight;
  const examMode = studyPlan?.examMode;
  const daysUntilExam = studyPlan?.daysUntilExam;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-heading">Today's Study Plan</CardTitle>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {examMode && examMode !== 'normal' && daysUntilExam !== null && daysUntilExam !== undefined && (
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full flex items-center gap-1 ${
                  examMode === 'intensive'
                    ? 'text-destructive bg-destructive/10'
                    : 'text-amber-600 dark:text-amber-400 bg-amber-500/10'
                }`}>
                  <CalendarClock className="w-3 h-3" />
                  Exam in {daysUntilExam}d
                </span>
              )}
              {totalMinutes > 0 && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" />~{totalMinutes} min
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* Time picker dropdown */}
            <div className="relative">
              <button
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-muted"
                onClick={() => setShowTimePicker(v => !v)}
                title="Change available time"
              >
                <Clock className="w-3.5 h-3.5" />
                <span>{availableMinutes} min</span>
                <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${showTimePicker ? 'rotate-180' : ''}`} />
              </button>
              {showTimePicker && (
                <div className="absolute right-0 top-full mt-1 bg-popover border border-border rounded-lg shadow-lg z-10 p-1 flex flex-col gap-0.5 min-w-[80px]">
                  {[20, 45, 60, 90].map(mins => (
                    <button
                      key={mins}
                      onClick={() => { onAvailableMinutesChange?.(mins); setShowTimePicker(false); setTimeChanged(true); }}
                      className={`text-xs px-3 py-1.5 rounded-md text-left transition-colors ${
                        availableMinutes === mins
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted text-foreground'
                      }`}
                    >
                      {mins} min
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Refresh icon button */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              title={timeChanged ? "Tap to apply your new time" : "Refresh plan"}
              className={`p-1.5 rounded-md transition-all disabled:opacity-50 ${
                timeChanged
                  ? 'text-primary bg-primary/10 animate-pulse hover:bg-primary/20'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {isRefreshing
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <RefreshCw className={`w-3.5 h-3.5 ${timeChanged ? 'text-primary' : ''}`} />
              }
            </button>
          </div>
        </div>
        {/* Yesterday adherence */}
        {yesterdayAdherence && yesterdayAdherence.total > 0 && (
          <p className="text-[10px] text-muted-foreground mt-1">
            Yesterday: {yesterdayAdherence.completed}/{yesterdayAdherence.total} completed
          </p>
        )}
        {/* Confirmation dialog inline */}
        {showConfirm && (
          <div className="flex items-center gap-2 text-xs mt-2">
            <span className="text-muted-foreground">You still have unfinished tasks. Refresh anyway?</span>
            <button
              type="button"
              onClick={async () => {
                setShowConfirm(false);
                await onRefreshPlan?.();
              }}
              disabled={isRefreshing || !onRefreshPlan}
              className="text-xs font-medium text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={() => setShowConfirm(false)}
              className="text-xs font-medium text-muted-foreground hover:underline"
            >
              Cancel
            </button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Start Here — Primary Action (first non-completed task) */}
        {primarySuggestion && (
          <div
            className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors group bg-primary/5 hover:bg-primary/10 border border-primary/20"
            onClick={() => {
              const tab = primarySuggestion.prescribedStudyMode?.tab || primarySuggestion.tab || 'resources';
              onNavigate(primarySuggestion.moduleId, primarySuggestion.chapterId, tab, primarySuggestion.subtab);
            }}
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-primary/10">
              {(() => { const Icon = getTaskIcon(primarySuggestion); return <Icon className="w-5 h-5 text-primary" />; })()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <TaskStatusDot status={getTaskStatus(dailyPlan, primarySuggestion.chapterId)} />
                {isCarriedOver(dailyPlan, primarySuggestion.chapterId) && (
                  <span className="text-[9px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded flex items-center gap-0.5">
                    <RotateCcw className="w-2.5 h-2.5" />carried
                  </span>
                )}
              </div>
              <p className="font-medium truncate text-foreground">{getChapterName(primarySuggestion)}</p>
              <p className="text-xs text-foreground/70">
                {getActivityLabel(primarySuggestion)}{primarySuggestion.estimatedMinutes ? ` · ~${primarySuggestion.estimatedMinutes} min` : ''}
              </p>
              {primarySuggestion.reason && getActivityLabel(primarySuggestion) !== primarySuggestion.reason && (
                <p className="text-[11px] text-muted-foreground">
                  {primarySuggestion.trend && primarySuggestion.trend !== 'stable' && (
                    <span className={`font-medium ${trendIndicator[primarySuggestion.trend]?.className || ''} mr-1`}>
                      {trendIndicator[primarySuggestion.trend]?.icon}
                    </span>
                  )}
                  {primarySuggestion.reason}</p>
              )}
            </div>
            <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
          </div>
        )}

        {/* Other suggestions */}
        {otherSuggestions.length > 0 && (
          <div className="space-y-1.5">
            {otherSuggestions.map((item, idx) => {
              const Icon = getTaskIcon(item);
              const taskStatus = getTaskStatus(dailyPlan, item.chapterId);
              const carried = isCarriedOver(dailyPlan, item.chapterId);
              const isCompleted = taskStatus === 'completed';
              return (
                <div
                  key={idx}
                  className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors group ${
                    isCompleted ? 'bg-muted/30 opacity-60' : 'bg-muted/50 hover:bg-muted'
                  }`}
                  onClick={() => {
                    const tab = item.prescribedStudyMode?.tab || item.tab || 'resources';
                    onNavigate(item.moduleId, item.chapterId, tab, item.subtab);
                  }}
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center bg-secondary">
                    <Icon className="w-4 h-4 text-secondary-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <TaskStatusDot status={taskStatus} />
                      <p className={`text-sm font-medium truncate text-foreground ${isCompleted ? 'line-through' : ''}`}>{getChapterName(item)}</p>
                      {carried && (
                        <span className="text-[9px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded flex items-center gap-0.5 flex-shrink-0">
                          <RotateCcw className="w-2.5 h-2.5" />carried
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-foreground/70">
                      {getActivityLabel(item)}{item.estimatedMinutes ? ` · ~${item.estimatedMinutes} min` : ''}
                    </p>
                    {item.reason && getActivityLabel(item) !== item.reason && (
                      <p className="text-[11px] text-muted-foreground truncate">
                        {item.trend && item.trend !== 'stable' && (
                          <span className={`font-medium ${trendIndicator[item.trend]?.className || ''} mr-1`}>
                            {trendIndicator[item.trend]?.icon}
                          </span>
                        )}
                        {item.reason}</p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
                </div>
              );
            })}
          </div>
        )}

        {/* Confidence Insight */}
        {insight && (
          <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-accent/50 border border-accent">
            <Lightbulb className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
            <p className="text-xs text-foreground/80 leading-relaxed">{insight}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
